import { type TenantEnum } from '@jsmdg/tenancy';
import { type AlgoliaProduct } from '../client/types/types';
import { ProductType } from './enums';
import { Locale } from './locale';
import {
    type AlgoliaConfig,
    type ClosestSearchLocation,
    type ProductSearchItem,
    type ProductSearchLocationItem,
} from './types';

const getAlgoliaConfig = (productType: ProductType, userToken?: string | null): AlgoliaConfig => {
    return {
        environment: process.env.EVELIN_ENVIRONMENT as string,
        appId: process.env.ALGOLIA_APP_ID as string,
        searchKey: process.env.ALGOLIA_SEARCH_KEY as string,
        serverState: undefined,
        searchEnabled: productType === ProductType.BOX,
        userToken: userToken ?? undefined,
    };
};

const getLocaleFromString = (locale: string): Locale => {
    try {
        return locale.split('-')[0].toLowerCase() as Locale;
    } catch {
        return Locale.DE;
    }
};

const getClosestLocation = (product: AlgoliaProduct): ClosestSearchLocation | undefined => {
    // eslint-disable-next-line no-underscore-dangle
    if (!product?._rankingInfo?.matchedGeoLocation) {
        return undefined;
    }

    const matchedLocation = product.locations?.find(
        location =>
            location.position.lat.toFixed(2) ===
                // eslint-disable-next-line no-underscore-dangle
                product._rankingInfo?.matchedGeoLocation?.lat.toFixed(2) &&
            location.position.lon.toFixed(2) ===
                // eslint-disable-next-line no-underscore-dangle
                product._rankingInfo?.matchedGeoLocation?.lng.toFixed(2),
    );

    if (!matchedLocation) return undefined;

    return {
        distance: {
            unit: 'km',
            // distance is in meters
            // eslint-disable-next-line no-underscore-dangle
            value: Math.ceil(product?._rankingInfo?.matchedGeoLocation?.distance / 1_000),
        },
        lat: matchedLocation?.position.lat,
        long: matchedLocation?.position.lon,
        name: matchedLocation?.city,
    };
};

type IndexNameProps = {
    environment: string;
    tenant: TenantEnum;
    locale: string;
};

const getAlgoliaIndex = ({ environment, locale, tenant }: IndexNameProps): string => {
    return `${environment}_${tenant.toLocaleLowerCase()}_${getLocaleFromString(locale)}_product`;
};

const getRadius = (location?: ProductSearchLocationItem): number | 'all' => {
    if (location?.distance) {
        return location.distance * 1_000;
    }

    return 'all';
};

const getLatLng = (location?: ProductSearchLocationItem): string | undefined => {
    if (!location?.lat || !location.long) {
        return undefined;
    }

    return `${location.lat}, ${location.long}`;
};

type AlgoliaGiftBoxSearchOptions = {
    filters?: string;
    hitsPerPage?: number;
    getRankingInfo?: boolean;
    aroundLatLng?: string;
    aroundRadius?: number | 'all';
};
const getAlgoliaGiftBoxSearchOptions = (
    productId: string,
    inPreview: boolean,
    location?: ProductSearchLocationItem,
): AlgoliaGiftBoxSearchOptions => ({
    filters: `giftboxList:${productId} AND inPreview:${String(inPreview)}`,
    hitsPerPage: 12,
    getRankingInfo: true,
    aroundLatLng: getLatLng(location),
    aroundRadius: getRadius(location),
});

const mapAlgoliaProductToProduct = (product: AlgoliaProduct): ProductSearchItem => {
    return {
        ...product,
        locations: product.locations?.length
            ? product.locations.map(location => ({
                  name: location.city,
                  lat: location.position?.lat,
                  long: location.position?.lon,
              }))
            : [],
        price: {
            grossAmount: product.price.grossAmount,
            currencyCode: product.price.currencyCode,
        },
        originalPrice: product.originalPrice
            ? {
                  grossAmount: product.originalPrice.grossAmount,
                  currencyCode: product.originalPrice.currencyCode,
              }
            : undefined,
        closestLocation: getClosestLocation(product),
        minParticipants: product.participants?.min,
        maxParticipants: product.participants?.max,
        images: product.images.map(image => ({ ...image, src: image.url })),
    };
};

export {
    getAlgoliaConfig,
    getAlgoliaGiftBoxSearchOptions,
    getAlgoliaIndex,
    mapAlgoliaProductToProduct,
};

export type { AlgoliaGiftBoxSearchOptions };
